import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { ArrowUpRight, Eye } from "lucide-react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { API, createTranslator } from "@/lib/constants";
import {
  useAdminAuth,
  getAdminHeaders,
  setAdminSessionToken,
} from "@/lib/admin";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export default function AdminDashboardPage({ language }) {
  const t = createTranslator(language);
  const navigate = useNavigate();
  const authenticated = useAdminAuth(navigate);
  const [careerCount, setCareerCount] = useState(0);
  const [contactCount, setContactCount] = useState(0);
  const [photoCount, setPhotoCount] = useState(0);
  const [analytics, setAnalytics] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [resetStatus, setResetStatus] = useState("idle");

  useEffect(() => {
    if (!authenticated) return;
    const fetchData = async () => {
      try {
        const [careersRes, contactsRes, photosRes, analyticsRes] = await Promise.all([
          axios.get(`${API}/careers`, { headers: getAdminHeaders() }),
          axios.get(`${API}/admin/contact-requests`, { headers: getAdminHeaders() }),
          axios.get(`${API}/site-photos`),
          axios.get(`${API}/admin/analytics/summary`, { headers: getAdminHeaders() }).catch(() => ({ data: null })),
        ]);
        setCareerCount((careersRes.data || []).length);
        setContactCount((contactsRes.data || []).length);
        setPhotoCount((photosRes.data || []).length);
        if (analyticsRes.data) setAnalytics(analyticsRes.data);
      } catch (err) {
        console.error("Failed to load admin data:", err);
      }
      setIsLoading(false);
    };
    fetchData();
  }, [authenticated]);

  const handleLogout = async () => {
    await axios.post(`${API}/admin/logout`, {}, { headers: getAdminHeaders() });
    setAdminSessionToken(null);
    navigate("/admin-login");
  };

  const handleReset = async (event) => {
    event.preventDefault();
    if (!newPassword || newPassword.trim().length < 4) {
      setResetStatus("short");
      return;
    }
    setResetStatus("loading");
    try {
      await axios.post(
        `${API}/admin/reset-password`,
        { current_password: currentPassword, new_password: newPassword },
        { headers: getAdminHeaders() }
      );
      setAdminSessionToken(null);
      setResetStatus("success");
      setTimeout(() => navigate("/admin-login"), 2000);
    } catch (err) {
      if (err?.response?.status === 401) {
        setResetStatus("wrong_current");
      } else {
        setResetStatus("error");
      }
    }
  };

  if (!authenticated) return null;

  return (
    <div className="min-h-screen bg-[#f4f6fb]">
      <Header language={language} />
      <main className="pt-28 pb-20">
        <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row justify-between gap-8 items-start">
            <div>
              <p
                className="text-xs tracking-[0.3em] uppercase text-[#c2873c]"
                data-testid="admin-dashboard-eyebrow"
              >
                {t("adminDashboard.eyebrow", "Admin")}
              </p>
              <h1
                className="font-heading text-3xl sm:text-4xl text-[#003366] mt-4"
                data-testid="admin-dashboard-title"
              >
                {t("adminDashboard.title", "Admin Dashboard")}
              </h1>
            </div>
            <Button
              variant="outline"
              className="border-[#003366] text-[#003366] rounded-none px-4 py-2 text-xs font-bold uppercase tracking-widest hover:bg-[#003366] hover:text-white transition"
              onClick={handleLogout}
              data-testid="admin-dashboard-logout"
            >
              {t("adminDashboard.logout", "Log Out")}
            </Button>
          </div>

          {/* Analytics Card */}
          {analytics && (
            <div
              className="mt-6 bg-white border border-slate-200 shadow-sm p-6"
              data-testid="admin-analytics-card"
            >
              <div className="flex items-center gap-2 mb-4">
                <Eye className="text-[#003366]" size={20} />
                <h2 className="font-heading text-xl text-[#003366]">Visitor Analytics</h2>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center" data-testid="analytics-today">
                  <p className="font-heading text-3xl text-[#003366]">{analytics.today}</p>
                  <p className="text-xs uppercase tracking-wider text-slate-500 mt-1">Today</p>
                </div>
                <div className="text-center" data-testid="analytics-week">
                  <p className="font-heading text-3xl text-[#c2873c]">{analytics.this_week}</p>
                  <p className="text-xs uppercase tracking-wider text-slate-500 mt-1">This Week</p>
                </div>
                <div className="text-center" data-testid="analytics-month">
                  <p className="font-heading text-3xl text-emerald-600">{analytics.this_month}</p>
                  <p className="text-xs uppercase tracking-wider text-slate-500 mt-1">This Month</p>
                </div>
                <div className="text-center" data-testid="analytics-total">
                  <p className="font-heading text-3xl text-slate-700">{analytics.total}</p>
                  <p className="text-xs uppercase tracking-wider text-slate-500 mt-1">All Time</p>
                </div>
              </div>
            </div>
          )}

          <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            <Link
              to="/admin-dashboard/careers"
              className="bg-white border border-slate-200 shadow-sm p-6 hover:border-[#003366] transition-colors group"
              data-testid="admin-nav-careers"
            >
              <div className="flex items-center justify-between">
                <h2 className="font-heading text-2xl text-[#003366]">Career Submissions</h2>
                <ArrowUpRight className="text-[#003366] opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
              <p className="mt-2 text-sm text-slate-600">
                View all career applications with candidate details, messages, and resumes.
              </p>
              <div className="mt-6 flex items-baseline gap-2">
                <span
                  className="font-heading text-4xl text-[#003366]"
                  data-testid="admin-career-count"
                >
                  {isLoading ? "..." : careerCount}
                </span>
                <span className="text-sm text-slate-500 uppercase tracking-wider">submissions</span>
              </div>
            </Link>

            <Link
              to="/admin-dashboard/contacts"
              className="bg-white border border-slate-200 shadow-sm p-6 hover:border-[#c2873c] transition-colors group"
              data-testid="admin-nav-contacts"
            >
              <div className="flex items-center justify-between">
                <h2 className="font-heading text-2xl text-[#003366]">Documentation Requests</h2>
                <ArrowUpRight className="text-[#c2873c] opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
              <p className="mt-2 text-sm text-slate-600">
                View all contact form queries for company profile and documentation.
              </p>
              <div className="mt-6 flex items-baseline gap-2">
                <span
                  className="font-heading text-4xl text-[#c2873c]"
                  data-testid="admin-contact-count"
                >
                  {isLoading ? "..." : contactCount}
                </span>
                <span className="text-sm text-slate-500 uppercase tracking-wider">requests</span>
              </div>
            </Link>

            <Link
              to="/admin-dashboard/site-photos"
              className="bg-white border border-slate-200 shadow-sm p-6 hover:border-emerald-500 transition-colors group"
              data-testid="admin-nav-photos"
            >
              <div className="flex items-center justify-between">
                <h2 className="font-heading text-2xl text-[#003366]">Site Photos</h2>
                <ArrowUpRight className="text-emerald-500 opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
              <p className="mt-2 text-sm text-slate-600">
                Upload and manage ongoing project site photographs.
              </p>
              <div className="mt-6 flex items-baseline gap-2">
                <span
                  className="font-heading text-4xl text-emerald-600"
                  data-testid="admin-photo-count"
                >
                  {isLoading ? "..." : photoCount}
                </span>
                <span className="text-sm text-slate-500 uppercase tracking-wider">photos</span>
              </div>
            </Link>
          </div>

          <div
            className="mt-6 bg-white border border-slate-200 shadow-sm p-6 max-w-md"
            data-testid="admin-dashboard-reset"
          >
            <h3
              className="font-heading text-xl text-[#003366]"
              data-testid="admin-dashboard-reset-title"
            >
              Reset Admin Password
            </h3>
            <p className="mt-2 text-sm text-slate-600">
              Update the password for future logins.
            </p>
            <form className="mt-4 space-y-4" onSubmit={handleReset}>
              <Input
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="Current Password"
                className="rounded-none"
                data-testid="admin-dashboard-current-password"
              />
              <Input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="New Password"
                className="rounded-none"
                data-testid="admin-dashboard-new-password"
              />
              {resetStatus === "success" && (
                <p
                  className="text-sm text-emerald-600 font-medium"
                  data-testid="admin-dashboard-reset-success"
                >
                  Password updated successfully. All sessions invalidated. Redirecting to login...
                </p>
              )}
              {resetStatus === "wrong_current" && (
                <p className="text-sm text-red-600" data-testid="admin-dashboard-reset-error">
                  Current password is incorrect.
                </p>
              )}
              {resetStatus === "short" && (
                <p className="text-sm text-red-600" data-testid="admin-dashboard-reset-error">
                  New password must be at least 4 characters.
                </p>
              )}
              {resetStatus === "error" && (
                <p className="text-sm text-red-600" data-testid="admin-dashboard-reset-error">
                  Unable to update password. Try again.
                </p>
              )}
              <Button
                type="submit"
                className="rounded-none bg-[#003366] text-white hover:bg-[#002244]"
                disabled={resetStatus === "loading" || resetStatus === "success"}
                data-testid="admin-dashboard-reset-button"
              >
                {resetStatus === "loading"
                  ? "Updating..."
                  : resetStatus === "success"
                  ? "Redirecting..."
                  : "Update Password"}
              </Button>
            </form>
          </div>
        </section>
      </main>
      <Footer language={language} />
    </div>
  );
}
